import { useNavigate } from "react-router"
import { useAuth } from "@/lib/auth/auth-context"

/**
 * The amber "you are impersonating someone" bar, with the two ways out.
 *
 * Renders `null` unless an impersonation session is active, so it is safe to
 * drop into any authenticated shell unconditionally.
 *
 * Mounted per-shell rather than once globally: this app's chrome is
 * viewport-anchored (`app-sidebar` is `fixed top-0`, `AppLayout` is an
 * `h-dvh` flex column), and a `fixed` element ignores any offset applied to
 * an ancestor — so a single global bar would slide *under* the sidebar and
 * every full-height shell instead of displacing it. Each shell therefore
 * hosts the bar inside its own column, where it takes real layout space.
 * The markup and behaviour live here so there's still one source of truth.
 */
export function ImpersonationBanner({
  secondaryAction = "back-to-admin",
}: {
  /**
   * The non-destructive action. Inside the app, that's a trip back to the
   * admin console *without* dropping the session ("Back to Admin"); inside
   * the admin console itself that would be a no-op, so it points the other
   * way instead ("Open App").
   */
  secondaryAction?: "back-to-admin" | "open-app"
}) {
  const { user, isImpersonating, endImpersonation } = useAuth()
  const navigate = useNavigate()

  if (!isImpersonating || !user) return null

  return (
    <div className="bg-amber-400 text-amber-950 text-sm font-medium px-4 py-2 flex items-center justify-between gap-3 shrink-0">
      <span className="truncate">
        Impersonating <strong>{user.email}</strong>
        {user.admin ? " (Admin)" : ""}
      </span>
      <div className="flex items-center gap-3 shrink-0">
        <button
          onClick={() => {
            endImpersonation()
            navigate("/admin")
          }}
          className="bg-amber-950 text-amber-100 px-3 py-1 rounded-lg text-xs font-semibold hover:bg-amber-900 transition-colors"
        >
          End Impersonation
        </button>
        <button
          onClick={() =>
            navigate(secondaryAction === "open-app" ? "/dashboard" : "/admin")
          }
          className="underline hover:no-underline text-xs"
        >
          {secondaryAction === "open-app" ? "Open App" : "Back to Admin"}
        </button>
      </div>
    </div>
  )
}
